const CHECKBOX_FILTERS = ['topics', 'statuses'];
const SELECTED_CLASS = { topics: 'topic-filter__selected-topics', statuses: 'status-filter__selected-statuses' };

export default function entriesSearch({ translations, view }) {
  return {
    translations,
    clearButtonVisible: { search: false, topics: false, statuses: false, sort: true },
    selected: { topics: [], statuses: [], sort: 'random' },
    currentFocus: -1,
    items: {},
    trigger: {},
    activeView: view,

    init() {
      [...CHECKBOX_FILTERS, 'sort'].forEach((type) => {
        const filter = this.filterRef(type);
        if (!filter) return;
        this.items[type] = filter.querySelectorAll('[role="menuitem"]');
        this.trigger[type] = filter.querySelector('summary');
      });

      if (this.$refs.searchInput) {
        this.clearButtonVisible.search = this.$refs.searchInput.value.length > 0;
        this.$refs.searchInput.addEventListener('input', () => {
          this.clearButtonVisible.search = this.$refs.searchInput.value.length > 0;
        });
      }

      CHECKBOX_FILTERS.forEach((type) => {
        const filter = this.filterRef(type);
        if (!filter) return;
        this.updateSelection(type);
        filter.addEventListener('toggle', () => {
          if (!filter.open) this.search();
        });
        filter.addEventListener('input', () => this.updateSelection(type));
      });

      if (this.$refs.sortFilter) {
        this.selected.sort = this.$refs.sortFilter.querySelector('input:checked')?.value ?? 'random';
        this.$refs.sortFilter.addEventListener('change', () => this.sort());
      }
    },

    filterRef(type) {
      return this.$refs[`${type}Filter`];
    },

    updateSelection(type) {
      this.selected[type] = this.filterRef(type).querySelectorAll('input:checked');
      this.clearButtonVisible[type] = this.selected[type].length > 0;
    },

    search() {
      this.$refs.searchForm.requestSubmit();
    },

    sort() {
      this.selected.sort = this.$refs.sortFilter.querySelector('input:checked').value;
      this.search();
      this.$refs.sortFilter.open = !this.$refs.sortFilter.open;
    },

    clearSearch() {
      this.$refs.searchInput.value = '';
      this.$refs.searchInput.focus();
      this.search();
    },

    clearCheckboxes(type) {
      this.filterRef(type).open = false;
      this.deselectAll(type);
      this.search();
    },

    selectItem(type) {
      const item = this.items[type][this.currentFocus].parentNode.querySelector('input');
      if (this.currentFocus > -1) item.checked = !item.checked;

      if (type === 'sort') {
        this.selected.sort = item.value;
        this.sort();
      } else {
        this.updateSelection(type);
      }
    },

    deselectAll(type) {
      this.selected[type].forEach((checkbox) => {
        checkbox.checked = false;
      });
      this.clearButtonVisible[type] = false;
      this.resetFocus(type);
      this.filterRef(type).open = false;
    },

    selectedText(type) {
      return `${this.translations[type].selected} (${this.selected[type].length})`;
    },

    previousDropdownItem(type) {
      if (this.currentFocus > (this.clearButtonVisible[type] ? 0 : 1)) {
        this.items[type][this.currentFocus].classList.remove('dropdown__item--focus');
        this.currentFocus--;
        this.items[type][this.currentFocus].classList.add('dropdown__item--focus');
      }
    },

    nextDropdownItem(type) {
      if (this.trigger[type].matches(':focus')) this.trigger[type].classList.add('summary--unfocused');

      if (this.currentFocus === -1 && !this.clearButtonVisible[type]) {
        this.currentFocus = 1;
        this.items[type][this.currentFocus].classList.add('dropdown__item--focus');
      } else {
        if (this.currentFocus < this.items[type].length - 1) {
          if (this.currentFocus > -1) this.items[type][this.currentFocus].classList.remove('dropdown__item--focus');
          this.currentFocus++;
          this.items[type][this.currentFocus].classList.add('dropdown__item--focus');
        }
      }
    },

    resetFocus(type) {
      if (this.items[type][this.currentFocus]) {
        this.items[type][this.currentFocus].classList.remove('dropdown__item--focus');
      }
      this.trigger[type].classList.remove('summary--unfocused');
      this.currentFocus = -1;
    },

    // Pressing Tab (9), Enter (13), Shift (16) or Space (32) should not trigger a search after 750ms.
    // Enter is here to prevent triggering search when pressing Enter in the skiplinks.
    // Pressing Enter in the search input will still work as expected (= post the search form).
    searchInput: {
      ['@keydown.debounce.750ms']() {
        if ([9, 13, 16, 32].includes(this.$event.keyCode)) return;
        this.search();
      },
    },

    clearInputButton: {
      ['@click']() {
        this.clearSearch();
      },
      ['@keydown.enter.stop']() {
        this.clearSearch();
      },
      ['x-show']() {
        return this.clearButtonVisible.search;
      },
    },

    clearCheckboxesButton(type) {
      return {
        ['@click.prevent']() {
          this.clearCheckboxes(type);
        },
        ['x-show']() {
          return this.clearButtonVisible[type];
        },
      };
    },

    checkboxPlaceholder(type) {
      return {
        ['x-text']() {
          return this.clearButtonVisible[type] ? this.selectedText(type) : this.translations[type].placeholder;
        },
        ['x-bind:class']() {
          return this.clearButtonVisible[type] ? SELECTED_CLASS[type] : '';
        },
        ['@keyup.tab']() {
          this.filterRef(type).open = false;
          this.search();
        },
      };
    },

    sortPlaceholder: {
      ['x-text']() {
        return this.translations.filters[this.selected.sort];
      },
      ['@click']() {
        this.$refs.sortFilter.open = false;
      },
      ['@keyup.enter']() {
        this.search();
      },
      ['@keyup.tab']() {
        this.search();
      },
    },

    checkboxDropdown(type) {
      return {
        ['@click']() {
          this.updateSelection(type);
        },
        ['@click.outside']() {
          this.filterRef(type).open = false;
        },
        ['@keydown.arrow-up.prevent']() {
          if (this.filterRef(type).open) this.previousDropdownItem(type);
        },
        ['@keydown.arrow-down.prevent']() {
          if (this.filterRef(type).open) this.nextDropdownItem(type);
        },
        ['@keydown.tab']() {
          this.resetFocus(type);
          this.filterRef(type).open = false;
        },
        ['@keydown.space.prevent']() {
          if (this.filterRef(type).open) this.resetFocus(type);
          this.filterRef(type).open = !this.filterRef(type).open;
        },
        ['@keydown.enter.prevent.stop']() {
          if (this.currentFocus > 0) {
            this.selectItem(type);
          } else if (this.currentFocus === 0) {
            this.deselectAll(type);
          }
        },
      };
    },

    sortDropdown: {
      ['x-cloak']() {
        return true;
      },
      ['x-transition']() {
        return true;
      },
      ['x-show']() {
        return this.activeView === 'grid';
      },
      ['@click.outside']() {
        this.$refs.sortFilter.open = false;
      },
      ['@keydown.arrow-up.prevent']() {
        if (this.$refs.sortFilter.open) this.previousDropdownItem('sort');
      },
      ['@keydown.arrow-down.prevent']() {
        if (this.$refs.sortFilter.open) this.nextDropdownItem('sort');
      },
      ['@keydown.tab']() {
        this.resetFocus('sort');
        this.$refs.sortFilter.open = false;
      },
      ['@keydown.space.prevent']() {
        if (this.$refs.sortFilter.open) this.resetFocus('sort');
        this.$refs.sortFilter.open = !this.$refs.sortFilter.open;
      },
      ['@keydown.enter.stop.prevent']() {
        if (this.currentFocus > -1) {
          this.selectItem('sort');
          this.resetFocus('sort');
        }
      },
    },

    shuffleTrigger: {
      ['@click']() {
        this.search();
      },
      ['@keydown.enter']() {
        this.search();
      },
    },
  };
}
